import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, Trash2, Image, Video, Save, ArrowLeft, Users, Grid, Settings, Link, Check, AlertCircle, Type, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ onClose }) => {
  const { refreshContent } = useLanguage();
  const [activeTab, setActiveTab] = useState('sections');
  const [gallery, setGallery] = useState([]);
  const [signups, setSignups] = useState([]);
  const [sectionContent, setSectionContent] = useState({});
  const [uploading, setUploading] = useState(false);
  const [videoUrls, setVideoUrls] = useState({
    hero: '',
    heat: '',
    cold: '',
    rest: '',
    gallery: ''
  });
  const [savingSection, setSavingSection] = useState(null);
  const [editableTexts, setEditableTexts] = useState({
    heroTagline: '',
    heroSubtitle: '',
    sanctuarySubline1: '',
    sanctuarySubline2: '',
  });
  const [savingText, setSavingText] = useState(false);
  const [legalTexts, setLegalTexts] = useState({
    impressum: '',
    datenschutz: '',
    streitbeilegung: '',
  });
  const [savingLegal, setSavingLegal] = useState(false);
  
  useEffect(() => {
    fetchGallery();
    fetchSignups();
    fetchContent();
    fetchTexts();
    fetchLegalTexts();
  }, []);
  
  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGallery(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };
  
  const fetchSignups = async () => {
    try {
      const response = await axios.get(`${API}/signup/early-bird`);
      setSignups(response.data);
    } catch (error) {
      console.error('Error fetching signups:', error);
    }
  };
  
  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      const contentMap = {};
      const urlMap = { hero: '', heat: '', cold: '', rest: '', gallery: '' };
      response.data.forEach(item => {
        contentMap[item.section] = item;
        if (item.video_url) {
          urlMap[item.section] = item.video_url;
        }
      });
      setSectionContent(contentMap);
      setVideoUrls(prev => ({ ...prev, ...urlMap }));
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };
  
  const fetchTexts = async () => {
    try {
      const response = await axios.get(`${API}/texts`);
      if (response.data) {
        setEditableTexts(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('Using default texts');
    }
  };
  
  const fetchLegalTexts = async () => {
    try {
      const response = await axios.get(`${API}/legal-texts`);
      if (response.data) {
        setLegalTexts(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('Using default legal texts');
    }
  };
  
  const handleTextSave = async () => {
    setSavingText(true);
    try {
      await axios.post(`${API}/texts`, editableTexts);
      toast.success('Texts saved! Refresh page to see changes.');
      if (refreshContent) refreshContent();
    } catch (error) {
      toast.error('Failed to save texts');
    } finally {
      setSavingText(false);
    }
  };
  
  const handleLegalTextSave = async () => {
    setSavingLegal(true);
    try {
      await axios.post(`${API}/legal-texts`, legalTexts);
      toast.success('Legal texts saved!');
    } catch (error) {
      toast.error('Failed to save legal texts');
    } finally {
      setSavingLegal(false);
    }
  };
  
  const handleVideoUrlSave = async (section) => {
    const url = videoUrls[section];
    if (!url) {
      toast.error('Please enter a video URL');
      return;
    }
    
    // Check if it's a direct video URL
    const isDirectVideoUrl = url.includes('.mp4') || 
                              url.includes('.webm') || 
                              url.includes('.mov') ||
                              url.includes('video-files') ||
                              url.includes('videos.pexels.com');
    
    if (!isDirectVideoUrl) {
      toast.error('Please use a direct video URL (ending in .mp4, .webm, or .mov). Pexels page URLs will not work!', {
        duration: 5000
      });
      return;
    }
    
    setSavingSection(section);
    try {
      await axios.post(`${API}/content/${section}/video-url`, {
        video_url: url
      });
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} video URL saved! Refresh the page to see changes.`);
      fetchContent();
      // Trigger global content refresh
      if (refreshContent) {
        refreshContent();
      }
    } catch (error) {
      toast.error('Failed to save video URL');
    } finally {
      setSavingSection(null);
    }
  };
  
  const handleFileUpload = async (e, type = 'image') => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    
    try {
      const uploadResponse = await axios.post(`${API}/upload`, formData);
      
      // Add to gallery
      const galleryItem = {
        type: type,
        url: uploadResponse.data.url,
        title: file.name.split('.')[0],
        order: gallery.length
      };
      
      await axios.post(`${API}/gallery`, galleryItem);
      toast.success('File uploaded successfully');
      fetchGallery();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  const handleDeleteGalleryItem = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`);
      toast.success('Item deleted');
      fetchGallery();
    } catch (error) {
      toast.error('Delete failed');
    }
  };
  
  const handleSectionVideoUpdate = async (section, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'video');
    
    try {
      const uploadResponse = await axios.post(`${API}/upload`, formData);
      
      await axios.put(`${API}/content/${section}`, {
        video_url: uploadResponse.data.url
      });
      
      toast.success(`${section} video updated`);
      fetchContent();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <motion.div 
      className="fixed inset-0 z-50 bg-[#030303] overflow-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-[#030303]/95 backdrop-blur-xl border-b border-white/5 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/5 rounded transition-colors"
              data-testid="admin-close"
            >
              <ArrowLeft className="w-5 h-5 text-white/60" />
            </button>
            <h1 className="font-cinzel text-xl tracking-[0.2em] uppercase text-white">
              Admin Panel
            </h1>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2">
            {[
              { id: 'gallery', icon: Grid, label: 'Gallery' },
              { id: 'sections', icon: Settings, label: 'Videos' },
              { id: 'texts', icon: Type, label: 'Texte' },
              { id: 'legal', icon: FileText, label: 'Legal' },
              { id: 'signups', icon: Users, label: 'Signups' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-xs tracking-[0.15em] uppercase transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-[#2bfecd]/10 text-[#2bfecd] border border-[#2bfecd]/30' 
                    : 'text-white/40 hover:text-white/60'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg tracking-[0.15em] uppercase text-white/80">
                Gallery Management
              </h2>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 px-6 py-3 border border-[#2bfecd]/30 text-[#2bfecd] text-xs tracking-[0.15em] uppercase cursor-pointer hover:bg-[#2bfecd]/10 transition-colors">
                  <Image className="w-4 h-4" />
                  Add Image
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={(e) => handleFileUpload(e, 'image')}
                    disabled={uploading}
                  />
                </label>
                <label className="flex items-center gap-2 px-6 py-3 border border-[#2bfecd]/30 text-[#2bfecd] text-xs tracking-[0.15em] uppercase cursor-pointer hover:bg-[#2bfecd]/10 transition-colors">
                  <Video className="w-4 h-4" />
                  Add Video
                  <input 
                    type="file" 
                    accept="video/*" 
                    className="hidden" 
                    onChange={(e) => handleFileUpload(e, 'video')}
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>
            
            {uploading && (
              <div className="mb-8 p-4 bg-[#2bfecd]/10 border border-[#2bfecd]/30">
                <p className="text-sm text-[#2bfecd] tracking-wider">Uploading...</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((item) => (
                <div key={item.id} className="relative group aspect-square bg-white/5">
                  {item.type === 'video' ? (
                    <video 
                      src={item.url.startsWith('/') ? `${BACKEND_URL}${item.url}` : item.url}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img 
                      src={item.url.startsWith('/') ? `${BACKEND_URL}${item.url}` : item.url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button 
                      onClick={() => handleDeleteGalleryItem(item.id)}
                      className="p-3 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors"
                    >
                      <Trash2 className="w-5 h-5 text-red-400" />
                    </button>
                  </div>
                  <div className="absolute top-2 left-2">
                    {item.type === 'video' ? (
                      <Video className="w-4 h-4 text-white/60" />
                    ) : (
                      <Image className="w-4 h-4 text-white/60" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {gallery.length === 0 && (
              <div className="text-center py-20 text-white/30">
                <p className="text-sm tracking-wider">No gallery items yet. Upload images or videos to get started.</p>
              </div>
            )}
          </div>
        )}
        
        {/* Sections Tab */}
        {activeTab === 'sections' && (
          <div className="space-y-8">
            <h2 className="text-lg tracking-[0.15em] uppercase text-white/80 mb-4">
              Section Video URLs
            </h2>
            
            {/* Help Box */}
            <div className="p-4 bg-[#2bfecd]/5 border border-[#2bfecd]/20 rounded mb-8">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#2bfecd] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white/70 mb-2">
                    <strong>Wichtig:</strong> Verwende direkte Video-URLs (die mit .mp4 enden)
                  </p>
                  <p className="text-xs text-white/50 mb-2">
                    <strong>Pexels Videos:</strong> Auf der Pexels Video-Seite → "Free Download" Button → Rechtsklick auf Auflösung → "Link-Adresse kopieren"
                  </p>
                  <p className="text-xs text-white/40">
                    Beispiel: https://videos.pexels.com/video-files/6698193/6698193-uhd_2732_1440_25fps.mp4
                  </p>
                </div>
              </div>
            </div>
            
            {['hero', 'heat', 'cold', 'rest', 'gallery'].map(section => (
              <div key={section} className="p-6 border border-white/10 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-cinzel text-xl tracking-[0.15em] uppercase text-white">
                    {section === 'gallery' ? 'The Sanctuary' : section}
                  </h3>
                </div>
                
                {/* URL Input */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1 relative">
                    <Link className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      type="text"
                      value={videoUrls[section]}
                      onChange={(e) => setVideoUrls(prev => ({ ...prev, [section]: e.target.value }))}
                      placeholder="https://videos.pexels.com/video-files/xxxxx/xxxxx-uhd_2560_1440_30fps.mp4"
                      className="w-full bg-white/5 border border-white/10 px-12 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50"
                    />
                  </div>
                  <button
                    onClick={() => handleVideoUrlSave(section)}
                    disabled={savingSection === section}
                    className="flex items-center gap-2 px-6 py-3 bg-[#2bfecd] text-black text-xs tracking-[0.1em] uppercase font-medium hover:bg-[#2bfecd]/90 transition-colors disabled:opacity-50"
                  >
                    {savingSection === section ? (
                      <>Saving...</>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Save
                      </>
                    )}
                  </button>
                </div>
                
                {/* Current Video Preview */}
                {sectionContent[section]?.video_url && (
                  <div className="mt-4 p-4 bg-black/30 border border-white/5">
                    <p className="text-xs text-white/40 mb-3">Current video:</p>
                    <div className="flex items-start gap-4">
                      <video 
                        src={sectionContent[section].video_url.startsWith('/') 
                          ? `${BACKEND_URL}${sectionContent[section].video_url}` 
                          : sectionContent[section].video_url
                        }
                        className="w-48 h-28 object-cover bg-black"
                        controls
                        muted
                      />
                      <div className="flex-1">
                        <p className="text-xs text-white/60 break-all">
                          {sectionContent[section].video_url}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Or Upload */}
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-xs text-white/30 mb-3">Or upload a video file:</p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/60 text-xs tracking-[0.1em] uppercase cursor-pointer hover:bg-white/5 transition-colors">
                    <Upload className="w-4 h-4" />
                    Upload Video
                    <input 
                      type="file" 
                      accept="video/*" 
                      className="hidden" 
                      onChange={(e) => handleSectionVideoUpdate(section, e)}
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Texte Tab */}
        {activeTab === 'texts' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg tracking-[0.15em] uppercase text-white/80">
                Editable Texts
              </h2>
              <button
                onClick={handleTextSave}
                disabled={savingText}
                className="flex items-center gap-2 px-6 py-3 bg-[#2bfecd] text-black text-xs tracking-[0.1em] uppercase font-medium hover:bg-[#2bfecd]/90 transition-colors disabled:opacity-50"
              >
                {savingText ? 'Saving...' : (
                  <>
                    <Check className="w-4 h-4" />
                    Save All Texts
                  </>
                )}
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Hero Tagline */}
              <div className="p-6 border border-white/10 bg-white/[0.02]">
                <label className="block text-xs tracking-[0.15em] uppercase text-white/60 mb-3">
                  Hero Tagline
                </label>
                <input
                  type="text"
                  value={editableTexts.heroTagline}
                  onChange={(e) => setEditableTexts(prev => ({ ...prev, heroTagline: e.target.value }))}
                  placeholder="Ritual. Discipline. Transformation."
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50"
                />
              </div>
              
              {/* Hero Subtitle */}
              <div className="p-6 border border-white/10 bg-white/[0.02]">
                <label className="block text-xs tracking-[0.15em] uppercase text-white/60 mb-3">
                  Hero Subtitle
                </label>
                <input
                  type="text"
                  value={editableTexts.heroSubtitle}
                  onChange={(e) => setEditableTexts(prev => ({ ...prev, heroSubtitle: e.target.value }))}
                  placeholder="Discover the ancient path"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50"
                />
              </div>
              
              {/* Sanctuary Subline 1 */}
              <div className="p-6 border border-white/10 bg-white/[0.02]">
                <label className="block text-xs tracking-[0.15em] uppercase text-white/60 mb-3">
                  Sanctuary Subline 1
                </label>
                <input
                  type="text"
                  value={editableTexts.sanctuarySubline1}
                  onChange={(e) => setEditableTexts(prev => ({ ...prev, sanctuarySubline1: e.target.value }))}
                  placeholder="Munich's First Mobile Sauna"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50"
                />
              </div>
              
              {/* Sanctuary Subline 2 */}
              <div className="p-6 border border-white/10 bg-white/[0.02]">
                <label className="block text-xs tracking-[0.15em] uppercase text-white/60 mb-3">
                  Sanctuary Subline 2
                </label>
                <input
                  type="text"
                  value={editableTexts.sanctuarySubline2}
                  onChange={(e) => setEditableTexts(prev => ({ ...prev, sanctuarySubline2: e.target.value }))}
                  placeholder="at Reitschule Munich"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Legal Tab */}
        {activeTab === 'legal' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg tracking-[0.15em] uppercase text-white/80">
                Legal Pages
              </h2>
              <button
                onClick={handleLegalTextSave}
                disabled={savingLegal}
                className="flex items-center gap-2 px-6 py-3 bg-[#2bfecd] text-black text-xs tracking-[0.1em] uppercase font-medium hover:bg-[#2bfecd]/90 transition-colors disabled:opacity-50"
              >
                {savingLegal ? 'Saving...' : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Legal Texts
                  </>
                )}
              </button>
            </div>
            
            <div className="p-4 bg-[#2bfecd]/5 border border-[#2bfecd]/20 rounded mb-8">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#2bfecd] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white/70 mb-2">
                    <strong>Tipp:</strong> Du kannst HTML-Formatierung verwenden für bessere Darstellung.
                  </p>
                  <p className="text-xs text-white/50">
                    Beispiel: &lt;strong&gt;Fett&lt;/strong&gt;, &lt;br&gt; für Zeilenumbruch, &lt;h3&gt;Überschrift&lt;/h3&gt;
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Impressum */}
              <div className="p-6 border border-white/10 bg-white/[0.02]">
                <label className="block text-xs tracking-[0.15em] uppercase text-white/60 mb-3">
                  Impressum / Imprint
                </label>
                <textarea
                  value={legalTexts.impressum}
                  onChange={(e) => setLegalTexts(prev => ({ ...prev, impressum: e.target.value }))}
                  placeholder="Angaben gemäß § 5 TMG:&#10;&#10;ICEBACH SAUNA&#10;Musterstraße 123&#10;80000 München&#10;..."
                  rows={12}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50 resize-y min-h-[200px]"
                />
              </div>
              
              {/* Datenschutz */}
              <div className="p-6 border border-white/10 bg-white/[0.02]">
                <label className="block text-xs tracking-[0.15em] uppercase text-white/60 mb-3">
                  Datenschutz / Privacy Policy
                </label>
                <textarea
                  value={legalTexts.datenschutz}
                  onChange={(e) => setLegalTexts(prev => ({ ...prev, datenschutz: e.target.value }))}
                  placeholder="Datenschutzerklärung&#10;&#10;1. Verantwortlicher&#10;Verantwortlich für die Datenverarbeitung auf dieser Website ist:&#10;..."
                  rows={12}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50 resize-y min-h-[200px]"
                />
              </div>
              
              {/* Streitbeilegung */}
              <div className="p-6 border border-white/10 bg-white/[0.02]">
                <label className="block text-xs tracking-[0.15em] uppercase text-white/60 mb-3">
                  Streitbeilegung / Dispute Resolution
                </label>
                <textarea
                  value={legalTexts.streitbeilegung}
                  onChange={(e) => setLegalTexts(prev => ({ ...prev, streitbeilegung: e.target.value }))}
                  placeholder="Hinweis zur Online-Streitbeilegung gemäß Art. 14 Abs. 1 ODR-VO:&#10;&#10;Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:&#10;https://ec.europa.eu/consumers/odr/&#10;..."
                  rows={8}
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50 resize-y min-h-[150px]"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Signups Tab */}
        {activeTab === 'signups' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg tracking-[0.15em] uppercase text-white/80">
                Early Bird Signups ({signups.length})
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-xs tracking-[0.15em] uppercase text-white/40">Name</th>
                    <th className="text-left py-4 px-4 text-xs tracking-[0.15em] uppercase text-white/40">Email</th>
                    <th className="text-left py-4 px-4 text-xs tracking-[0.15em] uppercase text-white/40">Phone</th>
                    <th className="text-left py-4 px-4 text-xs tracking-[0.15em] uppercase text-white/40">Language</th>
                    <th className="text-left py-4 px-4 text-xs tracking-[0.15em] uppercase text-white/40">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.map((signup) => (
                    <tr key={signup.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-4 px-4 text-sm text-white/80">{signup.name}</td>
                      <td className="py-4 px-4 text-sm text-white/60">{signup.email}</td>
                      <td className="py-4 px-4 text-sm text-white/60">{signup.phone || '-'}</td>
                      <td className="py-4 px-4 text-sm text-white/60 uppercase">{signup.language}</td>
                      <td className="py-4 px-4 text-sm text-white/40">
                        {new Date(signup.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {signups.length === 0 && (
              <div className="text-center py-20 text-white/30">
                <p className="text-sm tracking-wider">No signups yet.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AdminPanel;
